import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  styled,
} from "@mui/material";
import {
  AwaitableMergedPatientData,
  MergedPatientData,
} from "./patientVisitData";
import DialogContent from "@mui/material/DialogContent";
import moment from "moment";
import "moment/locale/he";
import { useClinicState } from "../../../../clinic/clinicStateContext";
import { SettingSwitch } from "fe-web-components/settings/presentaional/controls/settingSwitch";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import MailIcon from "@mui/icons-material/Mail";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { sendMessageWithCommunicationService } from "../../util/sendMessageWithCommmunicationService";
import { SupportedCommunicationPlatforms } from "fe-common/commonTypes/communicationLambdaApi";
import { MessageTouchPoint } from "@kahunmedical/common-configuration/dist/configuration/messageTemplate";
// TODO: read it from clinic config
//region [[ Styles ]]

const SendCaseLinksDialogView = styled(Dialog)({});

const ToggleContainer = styled("div")({
  display: "flex",
  flexFlow: "row",
  justifyContent: "flex-end",
  "& > *": { margin: "0 5px" },
});

const PatientContactDataAndStatus = styled("div")({
  display: "flex",
  flexFlow: "row",
  justifyContent: "space-evenly",
  width: "100%",
});

const Field = styled("div")({
  display: "flex",
  flexFlow: "column",
  flex: 1,
  alignItems: "flex-start",
  alignContent: "center",
  width: "50%",
  overflow: "hidden",
  padding: "0 5px",
  "&.align-right": {
    alignItems: "flex-end",
  },
});

//endregion [[ Styles ]]

//region [[ Props ]]

export interface SendCaseLinksDialogProps {
  open: boolean;
  onClose: () => void;
  patientInfo: AwaitableMergedPatientData[];
  selectedIds: string[];
  mode: SendCaseLinkMode;
  onItemRemoved: (p: MergedPatientData) => void;
}

export enum SendCaseLinkMode {
  Email = "email",
  Whatsapp = "wa",
}

enum SendStatus {
  None = "",
  Waiting = "Waiting...",
  Success = "Success!",
  Failure = "Error",
}

//endregion [[ Props ]]

//region [[ Functions ]]
const addCountryCode = (phoneNumber: string) => {
  let number = phoneNumber.replaceAll("-", "");
  if (number.startsWith("+")) return number.slice(1);
  if (number.startsWith("972")) return phoneNumber;
  if (number.startsWith("0")) return "972" + number.slice(1);
  return "972" + number;
};

const patientInfoToMacroFields = (pInfo: MergedPatientData) => ({
  patient_full_name: pInfo.firstName + " " + pInfo.lastName,
  patient_first_name: pInfo.firstName,
  patient_last_name: pInfo.lastName,
  visit_date: moment(pInfo.visitDateTime)
    .format("dddd, Do/MM/YYYY [בשעה] h:mm a")
    .replace('"', '\\"'),
  case_url: pInfo.caseUrl,
  clinic_name: pInfo.caseUrl?.split?.("/")?.[3] || "",
  patient_code_id: pInfo.caseUrl?.split?.("/")?.[4] || "",
  case_url_text: (pInfo.gender === "F" ? "לחצי" : "לחץ") + " כאן",
});
//endregion [[ Functions ]]

export const SendCaseLinksDialog = ({
  selectedIds,
  patientInfo,
  mode = SendCaseLinkMode.Email,
  ...props
}: SendCaseLinksDialogProps) => {
  moment().locale("he");

  const [currentMode, setMode] = useState(mode);

  const [
    {
      clinicData: { config, name: clinicName },
    },
  ] = useClinicState();

  const [sendStatuses, updateSendStatus] = useState<
    Record<string, SendStatus | string>
  >({});

  const [isSendingInProgress, setIsSendingInProgress] = useState(false);

  const filteredPatients = useMemo(
    () =>
      patientInfo?.filter(
        (pi) => !selectedIds?.length || selectedIds.some((si) => si === pi.id),
      ),
    [patientInfo, selectedIds],
  );

  const [overrideSendTo, setOverrideSendTo] = useState("");

  const startSendingMessage = (method: SupportedCommunicationPlatforms) => {
    updateSendStatus(
      filteredPatients.reduce(
        (acc, current) => ({
          ...acc,
          [current.id]: SendStatus.Waiting,
        }),
        {},
      ),
    );

    setIsSendingInProgress(true);

    let allPromises = [];

    filteredPatients.forEach((pInfo) => {
      allPromises.push(async () => {
        if (!pInfo.caseUrl) {
          const caseUrl = await pInfo.urlPromise;
          pInfo = { ...pInfo, caseUrl };
        }
        return sendMessageWithCommunicationService(clinicName, method, {
          touchPoint: MessageTouchPoint.SendConversationLink,
          to:
            overrideSendTo.trim() === ""
              ? method === SupportedCommunicationPlatforms.Whatsapp
                ? addCountryCode(pInfo.phoneNumber)
                : pInfo.email
              : overrideSendTo,
          locale: "he",
          macroFields: patientInfoToMacroFields(pInfo),
        })
          .then(async (response) => ({
            response,
            text: await response.text(),
          }))
          .then(({ response, text }) =>
            updateSendStatus((current) => ({
              ...current,
              [pInfo.id]:
                response.status === 200
                  ? SendStatus.Success
                  : SendStatus.Failure + response.status + text,
            })),
          )
          .catch((e) => {
            updateSendStatus((current) => ({
              ...current,
              [pInfo.id]: SendStatus.Failure + e.message,
            }));
          });
      });
    });

    Promise.all(allPromises.map((p) => p())).then(() =>
      setIsSendingInProgress(false),
    );
  };

  useEffect(() => {
    setMode(mode);
  }, [mode, setMode]);

  useEffect(() => {
    setOverrideSendTo("");
  }, [setOverrideSendTo]);

  return (
    <SendCaseLinksDialogView
      fullWidth
      open={props.open}
      onClose={props.onClose}
    >
      <DialogTitle>
        <ToggleContainer>
          <MailIcon />
          <SettingSwitch
            checked={currentMode === SendCaseLinkMode.Whatsapp}
            onChange={() =>
              setMode((fromMode) =>
                fromMode === SendCaseLinkMode.Whatsapp
                  ? SendCaseLinkMode.Email
                  : SendCaseLinkMode.Whatsapp,
              )
            }
          />
          <WhatsAppIcon />
        </ToggleContainer>
        <br />
        Send to{" "}
        {currentMode === SendCaseLinkMode.Email ? (
          <MailIcon />
        ) : (
          <WhatsAppIcon />
        )}{" "}
        <input
          value={overrideSendTo}
          onChange={(e) => setOverrideSendTo(e.target.value)}
        />
      </DialogTitle>
      <DialogContent>
        <PatientContactDataAndStatus>
          <Field>
            {currentMode === SendCaseLinkMode.Email ? "Email" : "Phone"}
          </Field>
          <Field>Name</Field>
          <Field>Status</Field>
          <Field></Field>
        </PatientContactDataAndStatus>
        {filteredPatients.map((pInfo) => (
          <PatientContactDataAndStatus key={pInfo.id}>
            <Field>
              {currentMode === SendCaseLinkMode.Email
                ? pInfo.email
                : pInfo.phoneNumber}
            </Field>
            <Field className={"align-right"}>
              {pInfo.firstName + " " + pInfo.lastName}
            </Field>
            <Field>{sendStatuses[pInfo.id] || ""}</Field>
            <Field className={"align-right"}>
              <IconButton onClick={() => props.onItemRemoved(pInfo)}>
                <RemoveCircleOutlineIcon />
              </IconButton>
            </Field>
          </PatientContactDataAndStatus>
        ))}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() =>
            startSendingMessage(
              currentMode === SendCaseLinkMode.Email
                ? SupportedCommunicationPlatforms.Email
                : SupportedCommunicationPlatforms.Whatsapp,
            )
          }
          disabled={isSendingInProgress}
        >
          שלח
        </Button>
      </DialogActions>
    </SendCaseLinksDialogView>
  );
};
